import { ChangeEvent, FocusEvent, MouseEvent, KeyboardEvent, forwardRef, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { paddingBase, stylesColors } from './input.constants';
/* eslint-disable-next-line */
export type inputSize = 'sm' | 'md' | 'lg';

export type inputStyle = 'primary' | 'variant';

export interface InputProps {
    id?: string;
    name: string;
    value?: string | number;
    defaultValue?: string | number;
    placeholder?: string;
    type?: string;
    checked?: boolean;
    error?: boolean;
    completed?: boolean;
    disabled?: boolean;
    onClick?(event: MouseEvent): void;
    onChange?(event: ChangeEvent<HTMLInputElement>): void;
    onKeyDown?(event: KeyboardEvent<HTMLInputElement>): void;
    onKeyUp?(event: KeyboardEvent<HTMLInputElement>): void;
    onBlur?(event: FocusEvent): void;
    onFocus?(event: FocusEvent): void;
    rounded?: 'standard' | 'full';
    className?: string;
    required?: boolean;
    size?: inputSize;
    style?: inputStyle;
    min?: string;
    max?: string;
    readonly?: boolean;
    autoFocus?: boolean;
    'data-test-id'?: string;
}

export const getInputContainerClasses = (
    props: Pick<InputProps, 'completed' | 'rounded' | 'className' | 'error' | 'disabled' | 'size' | 'style'>,
) => {
    const isRounded = props?.rounded ? props?.rounded === 'standard' : true;
    return twMerge(
        'w-full outline-none border text-sm placeholder-gray-300 overflow-hidden',
        props.completed ? 'text-black' : 'text-stone-500',
        paddingBase[props.size || 'md'],
        isRounded ? 'rounded' : 'rounded-full',
        !props.error && 'border-gray-100 hover:border-zinc-500 active:border-zinc-500',
        stylesColors[props.style || 'primary'],
        props.error && 'border-error-200 hover:border-error-200 active:border-error-200 focus:border-error-200',
        props.disabled && 'border-gray-50 hover:border-gray-50 active:border-gray-50 focus:border-gray-50 bg-gray-50',
        props.className,
    );
};
export const Input = forwardRef<HTMLInputElement, InputProps>((props, ref) => {
    const classes = getInputContainerClasses(props);
    const localRef = useRef<HTMLInputElement | null>(null);
    const inputRef = ref || localRef;

    return (
        <input
            ref={inputRef}
            id={props?.id}
            name={props.name}
            value={props.value}
            defaultValue={props.defaultValue}
            placeholder={props.placeholder}
            type={props.type}
            onChange={props.onChange}
            onKeyDown={props.onKeyDown}
            onKeyUp={props.onKeyUp}
            onBlur={props.onBlur}
            onFocus={props.onFocus}
            onClick={props.onClick}
            disabled={props.disabled}
            className={classes}
            required={props.required || false}
            autoComplete="off"
            min={props.min}
            max={props.max}
            readOnly={props.readonly}
            autoFocus={props.autoFocus || false}
            checked={props.checked}
            data-test-id={props['data-test-id']}
        />
    );
});

Input.defaultProps = {
    error: false,
    disabled: false,
    rounded: 'standard',
    style: 'primary',
};

export default Input;
